import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button, Col, Nav, Row } from "react-bootstrap";
import { createColumnHelper } from "@tanstack/react-table";
import { GenericTable } from "components/GenericTable";
import { PAGE_SIZES } from "utils/pageSizeConfig";
import { createElementIdFactory, createReusableTableElementIds } from "utils/elementIds";
import { storeBrowseIds } from "utils/browseIdSession";
import "containers/common/List.css";
import "containers/common/Table.css";
import "containers/slm/shared/SLMSearchListCheckbox.css";

function SLMLeaseList({ isAuthenticated, data, search_count }) {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(true);
  const [leases, setLeases] = useState([]);

  useEffect(() => {
    if (!isAuthenticated) {
      return;
    }

    setIsLoading(false);
  }, [isAuthenticated]);

  useEffect(() => {
    setLeases(Array.isArray(data) ? data : []);
  }, [data]);

  const columnHelper = useMemo(() => createColumnHelper(), []);

  const createId = useMemo(() => createElementIdFactory("slm-lease-search"), []);
  const tableElementIds = useMemo(() => createReusableTableElementIds(createId), [createId]);

  const handleCheckboxChange = (rowIndex, checked) => {
    setLeases((previous) =>
      previous.map((record, index) => {
        if (index !== rowIndex) {
          return record;
        }
        return { ...record, selected: checked, modified: true };
      })
    );
  };

  const selectAll = () => {
    setLeases((previous) => previous.map((record) => ({ ...record, selected: true, modified: true })));
  };

  const deselectAll = () => {
    setLeases((previous) => previous.map((record) => ({ ...record, selected: false, modified: true })));
  };

  const columns = useMemo(
    () => [
      columnHelper.accessor("selected", {
        id: "selected",
        header: "",
        enableSorting: false,
        size: 60,
        meta: { align: "center" },
        cell: (info) => (
          <div className="d-flex justify-content-center align-items-center w-100">
            <input
              className="form-check-input position-static m-0"
              checked={Boolean(info.getValue())}
              type="checkbox"
              onChange={(event) => handleCheckboxChange(info.row.index, event.target.checked)}
            />
          </div>
        ),
      }),
      columnHelper.accessor("lease_id", {
        id: "lease_id",
        header: "Lease ID",
        size: 80,
        cell: (info) => {
          const clickedId = info.getValue();
          const selectedIds = (leases || []).filter((lease) => lease.selected).map((lease) => lease.lease_id);
          const browseIds = selectedIds.length > 0 ? selectedIds : [clickedId];
          const targetPath = `/slmleases/${clickedId}`;

          return (
            <a
              href={targetPath}
              onClick={(event) => {
                event.preventDefault();
                storeBrowseIds("slm-lease-browse-ids", browseIds);
                navigate(targetPath, { state: { data: browseIds } });
              }}
            >
              {clickedId}
            </a>
          );
        },
      }),
      columnHelper.accessor("building_name", {
        id: "building_name",
        header: "Building Name",
        size: 160,
      }),
      columnHelper.accessor("street_address", {
        id: "street_address",
        header: "Address",
        size: 220,
      }),
      columnHelper.accessor("suburb", {
        id: "suburb",
        header: "Suburb",
        size: 120,
      }),
      columnHelper.accessor("state_code", {
        id: "state_code",
        header: "State",
        size: 80,
      }),
      columnHelper.accessor("postcode", {
        id: "postcode",
        header: "Postcode",
        size: 80,
      }),
      columnHelper.accessor("floor_unit", {
        id: "floor_unit",
        header: "Suite/Level",
        size: 90,
      }),
      columnHelper.accessor("lease_type", {
        id: "lease_type",
        header: "Type",
        size: 90,
      }),
      columnHelper.accessor("stat_date", {
        id: "stat_date",
        header: "Stat Date",
        cell: (info) => {
          const value = info.getValue();
          if (!value) {
            return "";
          }
          const date = new Date(value);
          if (Number.isNaN(date.getTime())) {
            return "";
          }
          const day = String(date.getDate()).padStart(2, "0");
          const month = String(date.getMonth() + 1).padStart(2, "0");
          const year = date.getFullYear();
          return `${day}-${month}-${year}`;
        },
        size: 100,
      }),
      columnHelper.accessor("rent", {
        id: "rent",
        header: "Rent Per SQM",
        size: 120,
      }),
      columnHelper.accessor("tenant_area", {
        id: "tenant_area",
        header: "Area",
        size: 80,
      }),
      columnHelper.accessor("rent_per_annum", {
        id: "rent_per_annum",
        header: "Rent Per Annum",
        size: 130,
      }),
      columnHelper.accessor("broad_space_use_code", {
        id: "broad_space_use_code",
        header: "Broad Space",
        size: 120,
      }),
      columnHelper.accessor("space_use_name", {
        id: "space_use_name",
        header: "Space Use",
        size: 120,
      }),
      columnHelper.accessor("agent", {
        id: "agent",
        header: "Agent",
        size: 120,
      }),
      columnHelper.accessor("agency", {
        id: "agency",
        header: "Agency",
        size: 120,
      }),
      columnHelper.accessor("lessee", {
        id: "lessee",
        header: "Lessee",
        size: 140,
      }),
    ],
    [columnHelper, leases, navigate]
  );

  if (!isAuthenticated) {
    return null;
  }

  return (
    <div className="SLMLeaseList slm-search-list">
      <Row>
        <Col className="text-left">
          <Nav className="justify-content-start">
            <Nav.Item>
              <Button variant="primary" type="button" size="sm" onClick={selectAll}>
                Select All
              </Button>{" "}
            </Nav.Item>
            <Nav.Item>
              <Button variant="primary" type="button" size="sm" onClick={deselectAll}>
                Deselect All
              </Button>{" "}
            </Nav.Item>
          </Nav>
        </Col>
      </Row>

      <Row>
        <Col>
          <b>
            {leases ? leases.length : "0"} of {leases && leases.length > 0 ? leases[0].result_count || leases.length : "0"} records returned.
          </b>
        </Col>
      </Row>

      <Row>
        <Col sm={12} md={12} lg={12}>
          {!isLoading && search_count === 0 && (
            <div className="alert alert-secondary">Begin by entering search criteria or just clicking 'Search'</div>
          )}

          {!isLoading && search_count > 0 && (
            <GenericTable
              columns={columns}
              data={leases || []}
              pageSize={PAGE_SIZES.slmLeaseSearch}
              enablePagination={true}
              hasHeaderSorting={true}
              section="slm-lease-search"
              elementIds={tableElementIds}
            />
          )}
        </Col>
      </Row>
    </div>
  );
}

export default SLMLeaseList;
